import React from 'react'
import styled from 'styled-components'

import { IconCheck } from '../../iconography/Icons/Icons'

export type CheckboxProps = Omit<React.HTMLAttributes<HTMLButtonElement>, 'onChange'> & {
  label?: string | React.ReactElement
  value?: string
  isChecked?: boolean
  isPartlyChecked?: boolean
  partialCheckType?: 'square' | 'circle'
  disabled?: boolean
  onChange(value: string, isChecked: boolean, e?: React.MouseEvent): void
  /**
   * @deprecated use data-test-id instead
   */
  testId?: string
  hint?: string
}

type ContainerProps = Pick<CheckboxProps, 'disabled'>

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  ${({ disabled }: ContainerProps) => ({
    pointerEvents: disabled ? 'none' : 'auto',
    opacity: disabled ? '0.5' : '1',
  })};

  &[data-hint='true'] {
    align-items: flex-start;
  }

  &[data-hint='false'] {
    align-items: center;
  }
`

const BoxWrapper = styled.button`
  display: flex;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  margin-right: 8px;
  padding: 0;

  border-radius: 4px;
  outline: none;
  cursor: inherit;

  ${({ theme }) => ({
    border: `1px solid ${theme.color.secondary200}`,
    background: theme.color.onPrimaryLight,
  })}

  ${Container} [data-disabled='false'] &:focus,
  ${Container} [data-disabled='false']:hover &{
    border-color: ${props => props.theme.color.primary600};
  }

  [data-hint='false'] & {
    margin-top: 0;
  }
`

const CheckMark = styled(IconCheck)`
  margin-bottom: 2px;
`

const PartlyCheckedMark = styled.div`
  width: 8px;
  height: 8px;
  border-radius: ${(props: { type: CheckboxProps['partialCheckType'] }) => (props.type === 'circle' ? '4px' : 0)};
  ${({ theme }) => ({
    background: theme.color.primary600,
  })}
`
type BoxAreaProps = Omit<CheckboxProps, 'onChange'>

const BoxArea = ({ isChecked, isPartlyChecked, id, partialCheckType, ...other }: BoxAreaProps) => (
  <BoxWrapper id={id} type={'button'} role={'checkbox'} aria-checked={isChecked} {...other}>
    {isChecked ? isPartlyChecked ? <PartlyCheckedMark type={partialCheckType} /> : <CheckMark /> : null}
  </BoxWrapper>
)

const Label = styled.span`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  ${({ theme }) => ({
    color: theme.color.onPrimaryDark,
  })}

  ${props => props.theme.media.mobile`
    font-size: 14px;
    line-height: 20px;
  `}
`

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Hint = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;

  color: ${({ theme }) => theme.color.onPrimaryDark};

  opacity: 0.48;
`

export const Checkbox: React.FC<CheckboxProps> = ({
  value,
  isChecked,
  isPartlyChecked,
  label,
  disabled,
  onChange,
  id = 'uuid',
  className,
  testId,
  partialCheckType = 'circle',
  hint,
  ...other
}) => (
  <Container
    disabled={!!disabled}
    onClick={e => !disabled && onChange(value || '', !isChecked, e)}
    className={className}
    data-test-id={testId}
    data-disabled={!!disabled}
    data-hint={!!hint}
  >
    <BoxArea
      id={id}
      isChecked={isChecked}
      isPartlyChecked={isPartlyChecked}
      partialCheckType={partialCheckType}
      {...other}
    />
    {label && (
      <LabelContainer>
        <Label>{label}</Label>
        {hint && <Hint>{hint}</Hint>}
      </LabelContainer>
    )}
  </Container>
)
